import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ThemeToggle, Button } from '../components/ui';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'One number', test: (p) => /\d/.test(p) },
  { key: 'special', label: 'One special character', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const { signup, currentUser, error: authError } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) navigate('/');
  }, [currentUser, navigate]);

  const passwordFailures = PASSWORD_RULES.filter(r => !r.test(password));
  const passwordValid = passwordFailures.length === 0;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) return;
    if (!emailValid) {
      setShowEmailError(true);
      return;
    }
    if (!passwordValid) {
      setShowRules(true);
      return;
    }
    setLoading(true);
    try {
      await signup(email.trim(), password, name.trim() || null);
      setSuccess(true);
    } catch (err) {
      if (err.message && !err.code) setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full rounded-lg border border-border-light dark:border-border bg-background-light dark:bg-background px-3 py-2.5 text-sm text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary";

  return (
    <div className="min-h-screen bg-surface-hover dark:bg-background flex items-center justify-center px-4 transition-colors duration-200">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <img
            src={theme === 'dark' ? "/logo.png" : "/logolight.png"}
            alt="SeyAI Insights Logo"
            className="mx-auto h-16 w-auto mb-4"
          />
        </div>

        <ThemeToggle className="absolute -top-4 right-4" />

        <div className="bg-surface dark:bg-background-gray rounded-lg p-8 border border-border-light dark:border-border">
          {success ? (
            <div className="space-y-4 text-center">
              <h1 className="text-lg font-semibold tracking-tight">Check your inbox</h1>
              <p className="text-sm text-text-muted-light dark:text-text-muted">
                We sent a verification link to <span className="text-text-primary-light dark:text-text-primary">{email}</span>.
                Your account has been created but has no permissions yet — an administrator will review access before you can sign in.
              </p>
              <Button variant="primary" size="md" className="w-full" onClick={() => navigate('/login')}>
                Back to sign in
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="text-lg font-semibold tracking-tight">Create an account</h1>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Full name (optional)"
                  autoComplete="name"
                  className={inputClass}
                />
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    onBlur={() => setShowEmailError(true)}
                    placeholder="Email address"
                    autoComplete="email"
                    className={inputClass}
                  />
                  {showEmailError && email.trim() && !emailValid && (
                    <p className="mt-1 pl-3 text-[11px] text-red-600 dark:text-danger">
                      Please enter a valid email address.
                    </p>
                  )}
                </div>
                <div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      onBlur={() => setShowRules(true)}
                      placeholder="Password"
                      autoComplete="new-password"
                      className={`${inputClass} pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-text-muted-light dark:text-text-muted hover:text-text-primary-light dark:hover:text-text-primary transition-colors cursor-pointer"
                    >
                      {showPassword ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                    </button>
                  </div>
                  {showRules && (
                    <div className="my-4">
                      <p className="text-[11px] font-medium text-text-muted-light dark:text-text-muted mb-1">Password must contain:</p>
                      <ul className="space-y-0.5 pl-3">
                        {PASSWORD_RULES.map(rule => {
                          const passed = rule.test(password);
                          return (
                            <li key={rule.key} className={`text-[11px] ${passed ? 'text-brand-primary' : 'text-red-600 dark:text-danger'}`}>
                              {rule.label}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  className="w-full"
                  disabled={loading || !emailValid || !passwordValid}
                >
                  {loading ? 'Creating account...' : 'Create account'}
                </Button>
              </form>

              <p className="text-xs text-center text-text-muted-light dark:text-text-muted">
                Already have an account?{' '}
                <Link to="/login" className="text-brand-primary hover:underline">Sign in</Link>
              </p>

              {(error || authError) && (
                <div className="bg-red-50 dark:bg-danger-light border border-red-300 dark:border-danger rounded-lg p-4">
                  <p className="text-red-600 dark:text-danger text-sm text-center">{error || authError}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
